// =============================================================================
//  Copyright : 2020 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : stats hooks
// =============================================================================
// ---Third-party imports
import React from 'react'
import {Icon} from 'semantic-ui-react'
import CsvDownloader from 'react-csv-downloader'

// ---Framework imports
import {log} from 'uix/logging'
import {dict, mapItems, sum, keys, values, uniq} from 'ldash'
import {Sel, Actions} from 'uix/registries'
import {useSelector, usePersistent, useMemo, useSummary, usePartContext} from 'uix/hooks'
import {DomainTable} from 'uix/domain/dtable'
import {prettyBytes, formatSI} from 'uix/utils'
import {TableSegment, XMenu, PortalTarget, AutoPortal} from 'uix/components'

import {getSubdomains} from 'uix/selectors'

// ---Local imports

// ---Exports
//~ const useFastDomainStats = ({}) => {}

const DSTATS_COLS = i18n => {
  let filesize_col = name => ({
    Header: i18n.span(name),
    accessor: name,
    Cell: ({value}) => (value ? prettyBytes(value) : ''),
    selectable: true,
    Footer: ({data}) => prettyBytes(sum(data.map(r => r[name])) || 0),
  })
  let count_col = name => ({
    Header: i18n.span(name),
    accessor: name,
    Cell: ({value}) => (value ? formatSI(value, 1) : ''),
    selectable: true,
    Footer: ({data}) => formatSI(sum(data.map(r => r[name])) || 0, 2),
  })

  return [
    count_col('totalmetadata'),
    count_col('structurecount'),
    count_col('imagecount'),
    filesize_col('sumimagesize'),
    filesize_col('sumorgimagesize'),
    filesize_col('sumimagecachesize'),
    count_col('ocrcount'),
    filesize_col('sumfulltextsize'),
    count_col('pdfcount'),
    filesize_col('sumpdfsize'),
    count_col('ftpackplaincount'),
    filesize_col('sumftpackplainsize'),
    count_col('ftpackaltocount'),
    filesize_col('sumftpackaltosize'),
    count_col('ftpackhtmlcount'),
    filesize_col('sumftpackhtmlsize'),
    count_col('placecount'),
    count_col('namecount'),
    count_col('topstructurecount'),
    count_col('totalreleasedmetadata'),
    count_col('digitizedcount'),
  ]
}

const CsvButton = ({dname, data}) => {
  /* generate CSV from list of dicts
  maybe move down to TranslatedTable (to understand visible columns)
  */
  let domains = useSelector(Sel.domains)
  let i18n = useSelector(Sel.i18n)

  // add long description as first column in output (depends on field domainname)
  let tabular_data = useMemo(() => {
    let tabular_data = values(data).map(d => ({
      description: domains.data[d.domainname]?.description,
      ...d,
    }))

    // translate column headers
    return tabular_data.map(d => dict(mapItems(d, (k, v) => [i18n.get(k), v])))
  }, [data])

  return (
    <XMenu.PopupItem icon="download" basic>
      <CsvDownloader datas={tabular_data} filename={`bestand_${dname}`}>
        <a href="#">Download CSV</a>
      </CsvDownloader>
      <CsvDownloader datas={tabular_data} filename={`bestand_${dname}`} separator=";">
        <a href="#">Download CSV (Excel)</a>
      </CsvDownloader>
    </XMenu.PopupItem>
  )
}

export const DomainTreeStats = ({menuPortal}) => {
  let [aggregated, setAggregated] = usePersistent('aggregated', false)
  let [dtypeFilter, setDtypeFilter] = usePersistent('dtypeFilter', [], 1)

  let {dname, i18n} = usePartContext()
  let dstats_cols = DSTATS_COLS(i18n)

  let subdomains = useSelector(Sel.subdomains)
  let domains = useSelector(Sel.domains)
  let {sumdata: domain_summary} = useSummary('domain', dname)

  let stats_data = useMemo(() => {
    let result = domain_summary // dict(mapItems(domain_summary, (odid, {domainname, ...data}) => [odid, data]))

    if (aggregated) {
      let field_names = dstats_cols.map(c => c.accessor)
      result = dict(
        subdomains.map(sd => {
          let odid = sd.id
          let data = result[sd.id]
          let domainname = domains.odid2name[odid]
          let sds = getSubdomains(domainname, domains)

          let aggregated_data = dict(
            field_names.map(name => [name, sum(sds.map(sd => result[sd.id]?.[name] ?? 0))])
          )
          // restore `domainname` field as in original data (important for .csv output)
          aggregated_data = {domainname, ...aggregated_data}
          return [odid, aggregated_data]
        })
      )
    }
    return result
  }, [dname, domain_summary, aggregated])

  let ddopts = uniq(subdomains.map(d => d.type)).map(dtype => ({
    value: dtype,
    text: i18n.getType('domain', dtype),
  }))

  let dnameFilter = useMemo(
    () =>
      subdomains
        .filter(d => dtypeFilter.length == 0 || dtypeFilter.includes(d.type))
        .map(d => d.name),
    [dtypeFilter, subdomains]
  )
  let menu = (
    <>
      <XMenu.Dropdown
        options={ddopts}
        value={dtypeFilter}
        onSetValue={setDtypeFilter}
        placeholder="Typen"
        multiple
      />
      <CsvButton dname={dname} data={stats_data} />
      <XMenu.Toggle
        icon="calculator"
        set={setAggregated}
        value={aggregated}
        title="Zahlen aggregieren"
      />
      <PortalTarget id="dstatsPortal" />
    </>
  )

  let filterText =
    subdomains.length > 1 && dnameFilter.length < subdomains.length
      ? `${dnameFilter.length} von `
      : ''
  return (
    <TableSegment
      header="Bestand"
      icon={['list', 'warehouse']}
      subheader={subdomains.length > 0 ? `über ${filterText}${subdomains.length} Domains` : ''}
      menu={menu}
    >
      <DomainTable
        flex
        compact
        data={stats_data}
        columns={dstats_cols}
        menuPortal="dstatsPortal"
        dnameFilter={dnameFilter}
      />
    </TableSegment>
  )
}
